import { useCallback } from "react";

import type { DayOfTheWeek, OverSiteControllerPeoplePriceInput } from "@/lib/gql/__generated__/graphql";
import { type OverSiteControllerPeopleNumInput } from "@/lib/gql/__generated__/graphql";

import type { ForArrayType, ForAutoGenerator } from "@/components/features/_model/types/auto-generator-for-view-model";
import { DAY_OF_THE_WEEK_LIST } from "@/components/features/items/edit/_shared/constants/day-of-the-week";

import type { FormOptionType } from "@/components/ui/form/_types/type";

import { useProductRoomFormModelContext } from "../../../../../../context";
import { createEmptyPriceItem } from "../../../_constants/empty-price-item";

import type { CheckedState } from "@radix-ui/react-checkbox";

export const useWeekdaySettings = () => {
	const { viewModel, onSetViewModel } = useProductRoomFormModelContext();
	const { overSiteControllerPeopleNum, capacityPeopleMaximum: _capacityPeopleMaximum } = viewModel;
	const capacityPeopleMaximum = _capacityPeopleMaximum.value;

	const weekend = overSiteControllerPeopleNum?.value?.[0];
	const weekday = overSiteControllerPeopleNum?.value?.[1];
	const weekdayPrices = weekday?.prices;

	const canAppendItem = weekdayPrices?.value.length
		? Number(weekdayPrices.value.at(-1)?.upperNum.value) < Number(capacityPeopleMaximum)
		: true;

	const availableWeekday = weekday?.availableWeekDays.value ?? [];
	const rawAvailableWeekday = availableWeekday.map((item) => item.value);
	const weekdayPriceFieldOptions = DAY_OF_THE_WEEK_LIST.map((item) => {
		return {
			...item,
			id: `weekday_price_${item.label}`,
		};
	});

	/**
	 * 平日価格のみを更新する（休日価格は更新しない）
	 */
	const onSetWeekdaySettings = useCallback(
		(input: ForAutoGenerator<OverSiteControllerPeopleNumInput, "">) => {
			if (weekend && weekday) {
				onSetViewModel([
					{
						key: "overSiteControllerPeopleNum",
						value: {
							isEdited: true,
							hasError: false,
							value: [weekend, input],
						},
					},
				]);
			}
		},
		[onSetViewModel, weekday, weekend],
	);

	const defaultValue = createEmptyPriceItem(Number(capacityPeopleMaximum));

	/**
	 * 平日価格に新しい項目を追加する
	 */
	const appendItem = useCallback(() => {
		if (weekday) {
			onSetWeekdaySettings({
				...weekday,
				prices: {
					isEdited: true,
					hasError: false,
					value: weekdayPrices ? [...weekdayPrices.value, defaultValue] : [defaultValue],
				} satisfies ForArrayType<OverSiteControllerPeoplePriceInput[]>,
			});
		}
	}, [weekday, onSetWeekdaySettings, weekdayPrices, defaultValue]);

	/**
	 * 平日価格の項目の内、特定の項目を削除する（index）
	 */
	const removeItem = useCallback(
		(index: number) => {
			if (weekday) {
				onSetWeekdaySettings({
					...weekday,
					prices: {
						isEdited: true,
						hasError: false,
						value: weekdayPrices ? weekdayPrices.value.filter((_, i) => i !== index) : [],
					} satisfies ForArrayType<OverSiteControllerPeoplePriceInput[]>,
				});
			}
		},
		[weekday, onSetWeekdaySettings, weekdayPrices],
	);

	/**
	 * 平日価格の項目の内、特定の項目の上限人数を更新する
	 */
	const onSetWeekendUpperNum = (input: string, index: number) => {
		if (weekday) {
			const copy = { ...weekday };

			const newValue = {
				...copy.prices,
				value: copy.prices.value.map((item, i) =>
					i === index
						? {
								price: item.price,
								upperNum: {
									isEdited: true,
									value: Number(input),
								},
							}
						: item,
				),
			};

			onSetWeekdaySettings({ ...weekday, prices: newValue });
		}
	};

	const onSetWeekendPriceInput = (input: number | undefined, index: number) => {
		if (weekday) {
			const copy = { ...weekday };

			const newValue = {
				...copy.prices,
				value: copy.prices.value.map((item, i) =>
					i === index
						? {
								upperNum: item.upperNum,
								price: {
									...item.price,
									amount: {
										isEdited: true,
										value: input!,
									},
								},
							}
						: item,
				),
			};

			onSetWeekdaySettings({ ...weekday, prices: newValue });
		}
	};

	/**
	 * 平日価格にする曜日を更新する（平日価格に設定されていない曜日は休日価格の曜日になるように更新する必要がある）
	 */
	const onSetAvailableWeekday = (checked: CheckedState, item: FormOptionType) => {
		if (weekend && weekday) {
			// 最新の平日にする曜日の設定
			const rawNewAvailableWeekday = checked
				? [...new Set([...rawAvailableWeekday, item.value as DayOfTheWeek])]
				: rawAvailableWeekday.filter((value) => value !== (item.value as DayOfTheWeek));

			// 最新の休日にする曜日の設定
			const rawNewWeekendAvailableWeekend = DAY_OF_THE_WEEK_LIST.filter(
				(item) => !rawNewAvailableWeekday?.includes(item.value),
			).map((item) => item.value);

			const newAvailableWeekday = rawNewAvailableWeekday.map((item) => {
				return {
					isEdited: true,
					value: item,
				};
			});

			const newAvailableWeekend = rawNewWeekendAvailableWeekend.map((item) => {
				return {
					isEdited: true,
					value: item,
				};
			});

			onSetViewModel([
				{
					key: "overSiteControllerPeopleNum",
					value: {
						isEdited: true,
						hasError: false,
						value: [
							{
								...weekend,
								availableWeekDays: {
									isEdited: true,
									hasError: false,
									value: newAvailableWeekend,
								},
							},
							{
								...weekday,
								availableWeekDays: {
									isEdited: true,
									hasError: false,
									value: newAvailableWeekday,
								},
							},
						],
					},
				},
			]);
		}
	};

	return {
		onSetAvailableWeekday,
		rawAvailableWeekday,
		weekdayPriceFieldOptions,
		onSetWeekendPriceInput,
		capacityPeopleMaximum: Number(capacityPeopleMaximum),
		weekdayPrices,
		appendItem,
		canAppendItem,
		removeItem,
		onSetWeekendUpperNum,
	};
};
